import { useState, useMemo, useEffect } from 'react'
import {
  Box, Typography, TextField, InputAdornment, Alert, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Paper, MenuItem, Stack, Skeleton,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import Inventory2 from '@mui/icons-material/Inventory2'
import { useApi } from '../../hooks/useApi'
import ExportMenu from '../../components/common/ExportMenu'

// FAR-47 Current Inventory screen. Reads aggregated inventory from the
// /inventory endpoint (one row per material per plant, batches summed).

const COLUMNS = [
  { id: 'materialNumber', label: 'Item #', numeric: false },
  { id: 'description', label: 'Description', numeric: false },
  { id: 'plant', label: 'Plant', numeric: false },
  { id: 'quantityCases', label: 'On Hand (Cases)', numeric: true },
  { id: 'originalUnit', label: 'Unit', numeric: false },
  { id: 'batchCount', label: 'Batches', numeric: true },
]

const fmt = (n) => typeof n === 'number' ? n.toLocaleString() : '—'

export default function CurrentInventory() {
  const { get, loading, error } = useApi()
  const [rows, setRows] = useState([])
  const [snapshotAt, setSnapshotAt] = useState('')
  const [search, setSearch] = useState('')
  const [plantFilter, setPlantFilter] = useState('1002')
  const [unitFilter, setUnitFilter] = useState('')
  const [orderBy, setOrderBy] = useState('materialNumber')
  const [order, setOrder] = useState('asc')

  useEffect(() => {
    let cancelled = false
    get('/inventory').then((data) => {
      if (cancelled || !data) return
      setRows(data.records || [])
      setSnapshotAt(data.snapshotAt || '')
    })
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const plants = useMemo(
    () => [...new Set(rows.map((r) => r.plant).filter(Boolean))].sort(),
    [rows],
  )
  const units = useMemo(
    () => [...new Set(rows.map((r) => r.originalUnit).filter(Boolean))].sort(),
    [rows],
  )

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return rows
      .filter((r) => {
        if (plantFilter && r.plant !== plantFilter) return false
        if (unitFilter && r.originalUnit !== unitFilter) return false
        if (q) {
          const hay = `${r.materialNumber} ${r.description}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
      .sort((a, b) => {
        let av = a[orderBy], bv = b[orderBy]
        if (typeof av === 'string') av = av.toLowerCase()
        if (typeof bv === 'string') bv = bv.toLowerCase()
        if (av < bv) return order === 'asc' ? -1 : 1
        if (av > bv) return order === 'asc' ? 1 : -1
        return 0
      })
  }, [rows, search, plantFilter, unitFilter, orderBy, order])

  const handleSort = (col) => {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(col)
  }

  const snapshotLabel = snapshotAt
    ? new Date(snapshotAt).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
      })
    : null

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Inventory2 color="primary" />
          <Typography variant="h5" fontWeight={700}>Current Inventory</Typography>
        </Stack>
        <ExportMenu />
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Aggregated inventory snapshot from SAP — one row per item, batches summed.
        {snapshotLabel && (
          <>
            {' '}Snapshot taken{' '}
            <Chip label={snapshotLabel} size="small" variant="outlined" sx={{ ml: 0.5 }} />
          </>
        )}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Search item # or description"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 280 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
        <TextField
          select size="small" label="Plant" value={plantFilter}
          onChange={(e) => setPlantFilter(e.target.value)}
          sx={{ width: 160 }}
        >
          <MenuItem value="">All Plants</MenuItem>
          {plants.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Unit" value={unitFilter}
          onChange={(e) => setUnitFilter(e.target.value)}
          sx={{ width: 120 }}
        >
          <MenuItem value="">All</MenuItem>
          {units.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
        </TextField>
        <Typography variant="body2" color="text.secondary">
          {filtered.length} item{filtered.length !== 1 ? 's' : ''}
        </Typography>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.numeric ? 'right' : 'left'}
                  sx={{ fontWeight: 700 }}
                >
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0
              ? Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {COLUMNS.map((col) => (
                      <TableCell key={col.id}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))
              : filtered.length === 0
                ? (
                    <TableRow>
                      <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {rows.length === 0 ? 'No inventory data uploaded yet.' : 'No items match the current filters.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                : filtered.map((row) => (
                    <TableRow key={`${row.plant}-${row.materialNumber}`} hover>
                      <TableCell>{row.materialNumber}</TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell>{row.plant}</TableCell>
                      <TableCell align="right">
                        {row.unconvertedUnit
                          ? <Typography color="warning.main" variant="body2" title="No case conversion available">{fmt(row.quantityCases)} ({row.originalUnit})</Typography>
                          : fmt(row.quantityCases)
                        }
                      </TableCell>
                      <TableCell>{row.originalUnit}</TableCell>
                      <TableCell align="right">{row.batchCount || 1}</TableCell>
                    </TableRow>
                  ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
